import {
  type EpocusProbeAssociation,
  type EpocusProbeAssociationQueryConstructor,
  RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
  useRxCollectionEpocusProbeAssociation,
} from "$global-providers/rxdb-provider/collections/epocus-probe-association";
import { useUser } from "$global-providers/user-provider/provider";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";
import { useRxData } from "rxdb-hooks";
import { useActiveProgramId } from "./use-active-program";

const MAX_AE_TITLE_LENGTH = 16;

/**
 * The backend stores the serial verbatim but matches on trimmed values, so a
 * serial saved with surrounding whitespace would persist happily and then never
 * match an incoming study. Every write path funnels through this.
 */
export const normaliseProbeSerial = (serial: string): string => serial.trim();

export const normaliseDeviceAeTitle = (aeTitle: string): string =>
  aeTitle.trim();

export type DeviceAeTitleProblem = "empty" | "too-long" | "invalid-characters";

export const deviceAeTitleProblem = (
  aeTitle: string,
): DeviceAeTitleProblem | null => {
  const normalised = normaliseDeviceAeTitle(aeTitle);
  if (!normalised || normalised.toLowerCase() === "unknown") return "empty";
  if (normalised.length > MAX_AE_TITLE_LENGTH) return "too-long";
  if (
    normalised.includes("\\") ||
    [...normalised].some(
      (char) => char.charCodeAt(0) < 0x20 || char.charCodeAt(0) === 0x7f,
    )
  ) {
    return "invalid-characters";
  }
  return null;
};

export type ProbeIdentityInput = { serial: string; aeTitle: string };

export const probeIdentityLabel = (
  association: Pick<
    EpocusProbeAssociation,
    "deviceSerialNumber" | "deviceAeTitle"
  >,
): string => association.deviceAeTitle || association.deviceSerialNumber;

export const useMyProbeAssociations = () => {
  const user = useUser();

  const query = useMemo<EpocusProbeAssociationQueryConstructor>(
    () => (collection) =>
      collection?.find({
        selector: { performerId: user.id },
        sort: [{ deviceSerialNumber: "asc" }],
      }),
    [user.id],
  );

  const { result, isFetching } = useRxData<EpocusProbeAssociation>(
    RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
    query,
  );

  return { associations: result, loading: isFetching };
};

/**
 * Every probe association in scope, not just the current user's. The To Document
 * queue needs these when serial-restricted triage is off, to tell a scan from a
 * colleague's probe (theirs to claim) apart from one no registered probe
 * accounts for (anyone's to claim).
 */
export const useAllProbeAssociations = () => {
  const query = useMemo<EpocusProbeAssociationQueryConstructor>(
    () => (collection) => collection?.find(),
    [],
  );

  const { result, isFetching } = useRxData<EpocusProbeAssociation>(
    RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
    query,
  );

  return { associations: result, loading: isFetching };
};

/** The reason a write was refused, or `null` when it succeeded. */
export type ProbeAssociationFailure =
  | "empty"
  | "invalid-ae-title"
  | "duplicate"
  | "unavailable"
  | "failed";

export const PROBE_ASSOCIATION_FAILURE_MESSAGES: Record<
  ProbeAssociationFailure,
  string
> = {
  empty: "Enter the probe's serial number or the device's own AE title.",
  "invalid-ae-title":
    "An AE title is 1 to 16 characters with no backslash, exactly as set on the device.",
  duplicate: "You've already linked that device.",
  unavailable: "Local database not ready. Please try again.",
  failed: "Couldn't save that device. Please try again.",
};

export const PROBE_SERIAL_HINT =
  "The serial number is usually printed on the body of the probe. It may also be shown in the probe's companion mobile app.";

export const DEVICE_IDENTITY_HELP: ReadonlyArray<{
  device: string;
  where: string;
}> = [
  { device: "Philips Lumify", where: "the AE title under Setup" },
  { device: "Philips Flash 5100", where: "the AE title in its DICOM setup" },
  {
    device: "Philips Compact 5000",
    where: "the Chip ID under System Management, entered as the serial number",
  },
  {
    device: "Other probes",
    where:
      "the serial number printed on the body of the probe, or shown in its companion app",
  },
];

const normaliseIdentity = ({ serial, aeTitle }: ProbeIdentityInput) => ({
  deviceSerialNumber: normaliseProbeSerial(serial),
  deviceAeTitle: normaliseDeviceAeTitle(aeTitle) || null,
});

const identityFailure = ({
  deviceSerialNumber,
  deviceAeTitle,
}: ReturnType<typeof normaliseIdentity>): ProbeAssociationFailure | null => {
  if (!deviceSerialNumber && !deviceAeTitle) return "empty";
  if (deviceAeTitle && deviceAeTitleProblem(deviceAeTitle)) {
    return "invalid-ae-title";
  }
  return null;
};

const sameIdentitySelector = (
  performerId: string,
  { deviceSerialNumber, deviceAeTitle }: ReturnType<typeof normaliseIdentity>,
) => ({
  performerId,
  $or: [
    ...(deviceSerialNumber ? [{ deviceSerialNumber }] : []),
    ...(deviceAeTitle ? [{ deviceAeTitle }] : []),
  ],
});

export const useManageProbeAssociations = () => {
  const user = useUser();
  const collection = useRxCollectionEpocusProbeAssociation();
  const activeProgramId = useActiveProgramId();
  const [pendingSerial, setPendingSerial] = useState<string | null>(null);

  const add = useCallback(
    async (
      input: ProbeIdentityInput,
    ): Promise<ProbeAssociationFailure | null> => {
      const identity = normaliseIdentity(input);
      const invalid = identityFailure(identity);
      if (invalid) return invalid;
      if (!collection) return "unavailable";

      setPendingSerial(probeIdentityLabel(identity));
      try {
        const existing = await collection
          .findOne({ selector: sameIdentitySelector(user.id, identity) })
          .exec();
        if (existing) return "duplicate";

        const now = new Date().toISOString();
        await collection.insert({
          id: nanoid(),
          ...identity,
          performerId: user.id,
          performerName: `${user.firstName} ${user.lastName}`.trim(),
          programId: activeProgramId,
          meta: {
            createdAt: now,
            updatedAt: now,
            createdByUserId: user.id,
            updatedByUserId: user.id,
          },
        });
        return null;
      } catch (error) {
        console.error("Failed to add probe association", {
          errorName: error instanceof Error ? error.name : typeof error,
        });
        return "failed";
      } finally {
        setPendingSerial(null);
      }
    },
    [collection, user.id, user.firstName, user.lastName, activeProgramId],
  );

  const update = useCallback(
    async (
      id: string,
      input: ProbeIdentityInput,
    ): Promise<ProbeAssociationFailure | null> => {
      const identity = normaliseIdentity(input);
      const invalid = identityFailure(identity);
      if (invalid) return invalid;
      if (!collection) return "unavailable";

      setPendingSerial(probeIdentityLabel(identity));
      try {
        const clash = await collection
          .findOne({ selector: sameIdentitySelector(user.id, identity) })
          .exec();
        if (clash && clash.id !== id) return "duplicate";

        const doc = await collection.findOne(id).exec();
        if (!doc) return "failed";

        await doc.patch({
          ...identity,
          meta: {
            ...doc.toJSON().meta,
            updatedAt: new Date().toISOString(),
            updatedByUserId: user.id,
          },
        });
        return null;
      } catch (error) {
        console.error("Failed to update probe association", {
          errorName: error instanceof Error ? error.name : typeof error,
        });
        return "failed";
      } finally {
        setPendingSerial(null);
      }
    },
    [collection, user.id],
  );

  const remove = useCallback(
    async (id: string): Promise<ProbeAssociationFailure | null> => {
      if (!collection) return "unavailable";
      try {
        const doc = await collection.findOne(id).exec();
        await doc?.remove();
        return null;
      } catch (error) {
        console.error("Failed to remove probe association", {
          errorName: error instanceof Error ? error.name : typeof error,
        });
        return "failed";
      }
    },
    [collection],
  );

  return { add, update, remove, pendingSerial };
};
